import React from "react";
import { usePermissions } from "./Context";
import { Requirement } from "./types";
import { ToolTip } from "elements/ToolTip";
import { useSelector } from "react-redux";
import { State } from "store";
import { Grant, useRolesAndPermisionsQuery } from "../generated/graphql";
import { Redirect, useTenants } from "polyrouter";
import { useAuth0 } from "@auth0/auth0-react";
import {
  userMetaKey,
  INITIAL_CLIENT,
  POLY_LENS_DESKTOP,
  DEVELOPMENT,
  DEV
} from "Constants";

/**
 * Wrap a component with a Requirement.
 * @param requirement
 * @param children
 * @param reqName
 * @param redirectUrl
 * @constructor
 */
export const Guard: React.FC<{
  requirement: Requirement;
  reqName?: string | Requirement;
  redirectUrl?: string;
}> = ({ requirement, children, reqName, redirectUrl }) => {
  const allowed = useGuard(requirement);
  const debugUserRoles = useSelector((state: State) => {
    return state.env.debugUserRoles;
  });
  const environment = useSelector((state: State) => {
    return state.env.envName;
  });
  const debugEnv = environment === DEVELOPMENT || environment === DEV;

  const permissionsMessage =
    typeof reqName === "string" ? reqName : "a complex permission";

  if (!allowed && redirectUrl) {
    return <Redirect to={redirectUrl} />;
  }

  return (
    <>
      {debugEnv && debugUserRoles && (
        <ToolTip iconTitle="help" className="debug-tooltip">
          <div data-testid="debug-tooltip-message">
            {`Needs ${permissionsMessage}`}
          </div>
        </ToolTip>
      )}
      {allowed && children}
    </>
  );
};

export const useGuard = (requirement: Requirement) => {
  const hasPermission = usePermissions();
  return requirement(hasPermission);
};

export const protect = (requirement: Requirement) => {
  return function<T>(
    Component: React.ComponentType<T>
  ): React.ComponentType<T> {
    return (props: T) => (
      <Guard requirement={requirement}>
        <Component {...props} />
      </Guard>
    );
  };
};

export function withGuard<T extends object>(
  Component: React.ComponentType<T>
): React.ComponentType<T & { requirement: Requirement }> {
  return ({ requirement, ...props }: T & { requirement: Requirement }) => (
    <Guard requirement={requirement}>
      {/*W know that our props in are T and requirement to a component that needs just T where we already removed requirements, this cast is safe.*/}
      <Component {...(props as T)} />
    </Guard>
  );
}

function hasDeviceUserRoleOnly(grants: Grant[]) {
  let hasDeviceUserRole = false;
  let hasOtherRoles = false;
  grants.forEach((grant: Grant) => {
    if (grant.roles.find(role => role.name === "device_user")) {
      hasDeviceUserRole = true;
    }

    if (grant.roles.some(role => role.name !== "device_user")) {
      hasOtherRoles = true;
    }
  });
  return hasDeviceUserRole && !hasOtherRoles;
}

/**
 * Returns true if a user is only a desktop user,
 * false if user is also a Lens web user
 */
export const useDesktopUserOnly = (): Boolean => {
  const { user } = useAuth0();
  const userId = user ? user.sub : "";
  const tenantData = useTenants();
  const tenants = tenantData?.data?.tenants;
  const overrideDesktop = useSelector((state: State) => {
    return state.desktop?.overrideDesktopRoute ?? null;
  });

  const { data } = useRolesAndPermisionsQuery({
    variables: {
      accessorId: userId
    },
    pause: !userId
  });

  const grants = data?.accessor?.grants;

  if (user) {
    if (overrideDesktop) {
      return false;
    }

    if (
      tenants &&
      tenants.length === 0 &&
      user[userMetaKey][INITIAL_CLIENT] === POLY_LENS_DESKTOP
    ) {
      return true;
    }

    if (tenants && tenants.length > 0 && grants && grants.length > 0) {
      return hasDeviceUserRoleOnly(grants as Grant[]);
    }
  }

  return false;
};
